import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { FeaturedProject } from "config"
import CollectionFeatureFilters from "components/CollectionFeatureFilters"
import Loading from "components/Loading"
import MetadataTokenGrid from "components/MetadataTokenGrid"
import useArtBlocksProjectFeatureFacets from "hooks/useArtBlocksProjectFeatureFacets"
import useArtBlocksProjectTokens from "hooks/useArtBlocksProjectTokens"
import useWindowSize from "hooks/useWindowSize"
import {
  decodeFeatureFilters,
  encodeFeatureFilters,
  updateSearchParamsWithFeatureFilters,
} from "utils/featureFilters"
import { getCollectionGridTokenWidth } from "utils/galleryLayout"
import { formatNumber } from "utils/projectDisplay"
import { OrderDirection } from "utils/types"

const COLLECTION_PAGE_SIZE = 24

interface Props {
  project: FeaturedProject
}

const FeaturedProjectCollection = ({ project }: Props) => {
  const windowSize = useWindowSize()
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentPage, setCurrentPage] = useState(0)
  const [orderDirection, setOrderDirection] = useState<OrderDirection>(OrderDirection.ASC)
  const [renderMode, setRenderMode] = useState<"image" | "live">("image")
  const featureFilters = useMemo(() => decodeFeatureFilters(searchParams), [searchParams])
  const featureFilterKey = useMemo(() => encodeFeatureFilters(featureFilters), [featureFilters])
  const skip = currentPage * COLLECTION_PAGE_SIZE
  const { loading, error, data } = useArtBlocksProjectTokens(
    project,
    COLLECTION_PAGE_SIZE,
    skip,
    orderDirection,
    featureFilters
  )
  const facetsResponse = useArtBlocksProjectFeatureFacets(project.contractAddress, project.projectId, project.chainId)
  const tokens = data?.tokens || []
  const tokenCount = data?.tokens_aggregate?.aggregate?.count || data?.invocations || 0
  const totalCollectionCount = facetsResponse.data?.totalCount || data?.invocations || tokenCount
  const totalPages = Math.max(1, Math.ceil(tokenCount / COLLECTION_PAGE_SIZE))
  const aspectRatio = data?.aspect_ratio || project.aspectRatio || 1
  // The feature-filter sidebar only renders when the collection has facets.
  // Without this guard the lone content column lands in the 16rem filter slot
  // and the grid collapses (the dots-composites bug).
  const hasFeatureFilterPanel = facetsResponse.loading || (facetsResponse.data?.facets?.length || 0) > 0

  const tokenWidth = useMemo(() => {
    return getCollectionGridTokenWidth(windowSize.width)
  }, [windowSize.width])

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(0, Math.min(page, totalPages - 1)))
    window.requestAnimationFrame(() => {
      document.getElementById(`${project.key}-collection`)?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }

  useEffect(() => {
    setCurrentPage(0)
  }, [featureFilterKey])

  const handleFeatureFiltersChange = (nextFilters: typeof featureFilters) => {
    setCurrentPage(0)
    setSearchParams(updateSearchParamsWithFeatureFilters(searchParams, nextFilters))
  }

  return (
    <section id={`${project.key}-collection`} className="border-t border-gallery-border pt-10 md:pt-12 mt-12 md:mt-16">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <p className="gallery-label text-gallery-muted mb-3">{project.chainName} collection</p>
          <h2 className="gallery-subheading">Works</h2>
          <p className="mt-3 text-sm text-gallery-muted">
            {loading && tokenCount === 0 ? "Loading collection" : `${formatNumber(tokenCount)} sold works`}
          </p>
        </div>

        <div className="flex flex-col items-start sm:flex-row sm:items-center gap-4">
          <div className="gallery-segment-group" aria-label="Render mode">
            <button
              type="button"
              aria-pressed={renderMode === "image"}
              className="gallery-segment-button"
              onClick={() => setRenderMode("image")}
            >
              Image
            </button>
            <button
              type="button"
              aria-pressed={renderMode === "live"}
              className="gallery-segment-button"
              onClick={() => setRenderMode("live")}
            >
              Live
            </button>
          </div>

          <label className="gallery-select-wrap">
            <span>Sort</span>
            <select
              aria-label="Sort works"
              value={orderDirection}
              onChange={(e) => {
                setCurrentPage(0)
                setOrderDirection(e.target.value as OrderDirection)
              }}
              className="gallery-select"
            >
              <option value={OrderDirection.ASC}>Earliest</option>
              <option value={OrderDirection.DESC}>Latest</option>
            </select>
          </label>
        </div>
      </div>

      {error && <div className="gallery-empty text-red-500">Error loading collection.</div>}
      {loading && tokens.length === 0 && <Loading />}

      {(facetsResponse.loading || facetsResponse.data?.facets?.length || tokens.length > 0) && (
        <div className={hasFeatureFilterPanel ? "grid grid-cols-1 lg:grid-cols-[16rem_1fr] gap-8" : ""}>
          {hasFeatureFilterPanel && (
            <CollectionFeatureFilters
              facets={facetsResponse.data?.facets || []}
              selectedFilters={featureFilters}
              loading={facetsResponse.loading}
              error={facetsResponse.error}
              resultCount={tokenCount}
              totalCount={totalCollectionCount}
              truncated={facetsResponse.data?.truncated}
              onChange={handleFeatureFiltersChange}
            />
          )}

          <div>
            {tokens.length > 0 && (
              <MetadataTokenGrid
                tokens={tokens}
                tokenWidth={tokenWidth}
                fallbackAspectRatio={aspectRatio}
                renderMode={renderMode}
                project={project}
                getTokenHref={(token) => `${project.detailPath}/${token.token_id}`}
              />
            )}
          </div>
        </div>
      )}

      {tokenCount > COLLECTION_PAGE_SIZE && (
        <div className="flex flex-wrap justify-center items-center gap-4 mt-12 mb-8">
          <button
            aria-label="First works page"
            disabled={currentPage === 0}
            onClick={() => goToPage(0)}
            className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
          >
            First
          </button>
          <button
            aria-label="Previous works page"
            disabled={currentPage === 0}
            onClick={() => goToPage(currentPage - 1)}
            className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
          >
            Prev
          </button>
          <span className="gallery-label text-gallery-muted">
            {currentPage + 1} / {totalPages}
          </span>
          <button
            aria-label="Next works page"
            disabled={currentPage >= totalPages - 1}
            onClick={() => goToPage(currentPage + 1)}
            className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
          >
            Next
          </button>
          <button
            aria-label="Last works page"
            disabled={currentPage >= totalPages - 1}
            onClick={() => goToPage(totalPages - 1)}
            className="gallery-label disabled:opacity-30 hover:text-gallery-text transition-colors"
          >
            Last
          </button>
        </div>
      )}

      {!loading && !error && tokens.length === 0 && (
        <div className="gallery-empty">No sold works found for this collection.</div>
      )}
    </section>
  )
}

export default FeaturedProjectCollection
